import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/uikit/modal/Modal.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import styles from './WarrantyRequestModal.module.scss';
import clsx from 'clsx';
import {
  DeviceType,
  FillDefaultWarrantyRequestDto,
  WarrantyRequestBasketQuery,
  WarrantyRequestClient,
  WarrantyRequestQuery,
} from 'services/api/api-client.ts';
import {
  ProblemDetails,
  WarrantyResponseDto,
} from 'services/api/api-client.types.ts';
import { useWatch } from 'react-hook-form';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { SerialNumberEditing } from './components/SerialNumberEditing.tsx';
import { ArticleEditing } from './components/ArticleEditing.tsx';
import { ArticleInfo } from './components/ArticleInfo.tsx';
import { SerialNumberInfo } from './components/SerialNumberInfo.tsx';
import { convertAbasTable } from '../../../helpers/abas-helper.ts';
import ResetIcon from 'assets/icons/reset.svg?react';
import { getCurrentSupportedLanguage } from '../../../application/localization/localization.ts';
import { useAnalytics } from 'helpers/analytics.ts';
import { errorToString } from '../../../helpers/error-helpers.ts';
import { removeExtraLetters } from './warranty-helper.ts';

export const WarrantyRequestModalCategory = 'Warranty request modal';

export type Form = {
  serialNumber: string;
  serviceProductId?: string;
  operatingHours: number | null;
  dateOfResale: Date | null;
  article: string;
  articleSerialNumber: string;
  selectedErrors: Option[];
  selectedDevice: Option | undefined;
  errorDescription: string;
  areAnyErrorsSelected: boolean;
  isAnyDeviceSelected: boolean;
  isDurationSelected: boolean;
  duringTherapy: boolean;
  notDuringTherapy: boolean;
  formSubmitErrorText: string | null;
};

export type SameDeviceInfo = {
  serviceProductId: string;
  serialNumber: string;
};

type OwnProps = {
  sameDeviceInfo?: SameDeviceInfo;
  resetSameDeviceInfo: () => void;
  handleModalClose: () => void;
  handleModalSubmit: () => void;
  isOpen: boolean;
  title: string;
};

export type Option = {
  key: string;
  text: string;
};

const IdKey = 'serprod';
const CodeKey = 'serart';
const DescriptionKey = 'serartbez';

export const WarrantyRequestModal = (props: OwnProps) => {
  const {
    title,
    handleModalClose,
    isOpen,
    handleModalSubmit,
    sameDeviceInfo,
    resetSameDeviceInfo,
  } = props;
  const [isSerialNumberEditing, setIsSerialNumberEditing] = useState(
    !sameDeviceInfo,
  );
  const [isArticleEditing, setIsArticleEditing] = useState(true);
  const [isClearSerialNumberModalShown, setIsClearSerialNumberModalShown] =
    useState(false);
  const [isClearArticleModalShown, setIsClearArticleModalShown] =
    useState(false);
  const [isClearErrorsModalShown, setIsClearErrorsModalShown] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isCloseAddingModalShown, setIsCloseAddingModalShown] = useState(false);

  const i18n = useScopedTranslation('Page.WarrantyRequest');
  const currentLanguage = getCurrentSupportedLanguage(i18n.i18n);

  const defaultDeviceQuery = WarrantyRequestClient.addToBasketDefault;
  const specialDeviceQuery = WarrantyRequestClient.addToBasketSpecial;
  const sameNumberDeviceQuery = WarrantyRequestClient.addToBasketWithSameNum;

  const { trackEvent } = useAnalytics();

  const sameDevice = WarrantyRequestBasketQuery.useSearchLastAddedDeviceQuery(
    {
      serviceProduct: sameDeviceInfo?.serviceProductId,
    },
    {
      staleTime: 60 * 1000,
      enabled: !!sameDeviceInfo?.serviceProductId,
      throwOnError: false,
    },
  );
  const form = useAdvancedForm<Form>(
    async () => {
      try {
        setIsFormLoading(true);

        const baseDto: Omit<FillDefaultWarrantyRequestDto, 'serialNumber'> = {
          additionalErrorDescription: errorDescription,
          selectedErrors: selectedErrors.map(
            (selectedError) => selectedError.key,
          ),
          operatingHours: operatingHours ? Number(operatingHours) : null,
          resaleDate: dateOfResale,
          replacementPartSerialNumber: articleSerialNumber,
          replacementPartArticle: removeExtraLetters(article) ?? '',
        };

        if (deviceType === DeviceType.Special) {
          const specialDto = {
            ...baseDto,
            serialNumber: serialNumber,
            failureDuringTherapy: isDuringTherapy,
            noFailureDuringTherapy: isNotDuringTherapy,
          };

          await specialDeviceQuery(specialDto);
        } else if (selectedDevice || sameDeviceInfo?.serviceProductId) {
          const sameNumDto = {
            ...baseDto,
            serviceProduct:
              selectedDevice?.key ?? sameDeviceInfo?.serviceProductId ?? '',
          };

          await sameNumberDeviceQuery(sameNumDto);
        } else if (deviceType === DeviceType.Default) {
          const defaultDto = {
            ...baseDto,
            serialNumber: serialNumber,
          };

          await defaultDeviceQuery(defaultDto);
        } else {
          throw new Error('Unknown device type');
        }

        trackEvent({
          category: WarrantyRequestModalCategory,
          action: 'Add device to cart',
          name: `Serial number: ${serialNumber},
        PartNumber: ${baseDto.replacementPartSerialNumber},
        PartArticle: ${baseDto.replacementPartArticle}`,
        });

        handleModalSubmit();
      } catch (e) {
        form.setValue('formSubmitErrorText', errorToString(e));
        setIsFormLoading(false);
      }
    },
    {
      defaultValues: {
        serialNumber: sameDeviceInfo?.serialNumber ?? '',
        serviceProductId: sameDeviceInfo?.serviceProductId,
        dateOfResale: sameDevice.data?.resaleDate ?? null,
        operatingHours: sameDevice.data?.operatingHours ?? null,
        formSubmitErrorText: null,
        selectedErrors: [],
        duringTherapy: false,
        notDuringTherapy: false,
        selectedDevice: undefined,
      },
    },
  );

  const [
    serialNumber,
    article,
    dateOfResale,
    selectedErrors,
    articleSerialNumber,
    errorDescription,
    operatingHours,
    isDuringTherapy,
    isNotDuringTherapy,
    formSubmitErrorText,
    selectedDevice,
  ] = useWatch({
    control: form.control,
    name: [
      'serialNumber',
      'article',
      'dateOfResale',
      'selectedErrors',
      'articleSerialNumber',
      'errorDescription',
      'operatingHours',
      'duringTherapy',
      'notDuringTherapy',
      'formSubmitErrorText',
      'selectedDevice',
    ],
  });

  const device = WarrantyRequestQuery.useRequestDeviceQuery<
    WarrantyResponseDto,
    ProblemDetails
  >(
    {
      serialNumber: serialNumber,
      failureDuringTherapy: isDuringTherapy || undefined,
      noFailureDuringTherapy: isNotDuringTherapy || undefined,
      serviceProduct: selectedDevice?.key ?? sameDeviceInfo?.serviceProductId,
      language: currentLanguage,
    },
    {
      staleTime: 60 * 1000,
      enabled:
        (!!serialNumber && !isSerialNumberEditing) ||
        !!selectedDevice ||
        isDuringTherapy ||
        isNotDuringTherapy,
      throwOnError: false,
      retry: 0,
    },
  );
  const deviceType = sameDevice.data
    ? sameDevice.data.deviceType
    : device.data?.deviceType;

  const deviceTypes = useMemo(() => {
    const result: Option[] = [];

    if (deviceType !== DeviceType.WithMultipleSerialNumber) {
      return result;
    }

    const convertedTable = convertAbasTable(device.data?.table, {
      idField: 'zn',
      priorities: {
        serprod: 'value',
      },
    });

    if (!convertedTable) {
      return result;
    }

    for (const row of convertedTable) {
      result.push({
        key: row[IdKey],
        text: row[CodeKey] + ' ' + row[DescriptionKey],
      });
    }

    return result;
  }, [deviceType, device.data?.table]);

  const availableErrors = useMemo(() => {
    const result: Option[] = [];

    for (const key in device.data?.foundDevice?.availableErrors) {
      result.push({
        key,
        text: device.data?.foundDevice?.availableErrors[key],
      });
    }

    return result;
  }, [device.data?.foundDevice?.availableErrors]);

  const replacementPart = WarrantyRequestQuery.useSearchReplacementPartQuery(
    {
      replacementPartArticle: removeExtraLetters(article),
    },
    {
      staleTime: 60 * 1000,
      enabled: !!article && !isArticleEditing,
      throwOnError: false,
      retry: 1,
    },
  );
  const showClearArticleModal = () => {
    setIsClearArticleModalShown(true);
  };

  const showClearSerialNumberModal = () => {
    setIsClearSerialNumberModalShown(true);
  };

  const showClearErrorsModal = () => {
    setIsClearErrorsModalShown(true);
  };

  const removeSelectedError = (error: Option) => {
    const newSelectedErrors = selectedErrors.filter(
      (curErr) => curErr.key !== error.key,
    );

    form.setValue('selectedErrors', newSelectedErrors);
  };

  useEffect(() => {
    if (device.isError) {
      setIsSerialNumberEditing(true);
    }
  }, [device.isError]);

  useEffect(() => {
    if (replacementPart.isError) {
      setIsArticleEditing(true);
    }
  }, [replacementPart.isError]);

  const clearArticle = () => {
    trackEvent({
      category: WarrantyRequestModalCategory,
      action: 'Clear replacement part article',
      name: `Article: ${article}`,
    });
    setIsClearArticleModalShown(false);
    setIsArticleEditing(true);
    form.resetField('article');
    form.resetField('articleSerialNumber');
    form.resetField('errorDescription');
    form.setValue('selectedErrors', []);
    form.setValue('duringTherapy', false);
    form.setValue('notDuringTherapy', false);
    form.clearErrors('areAnyErrorsSelected');
    form.clearErrors('isDurationSelected');
  };

  const clearSerialNumber = () => {
    trackEvent({
      category: WarrantyRequestModalCategory,
      action: 'Clear serial number',
      name: `Serial number: ${serialNumber}`,
    });
    resetSameDeviceInfo();
    setIsClearSerialNumberModalShown(false);
    setIsSerialNumberEditing(true);
    setIsArticleEditing(true);
    form.reset();
  };

  const clearErrors = () => {
    trackEvent({
      category: WarrantyRequestModalCategory,
      action: 'Clear selected errors',
      name: 'Clear selected errors',
    });
    setIsClearErrorsModalShown(false);
    form.setValue('duringTherapy', false);
    form.setValue('notDuringTherapy', false);
    form.setValue('selectedErrors', []);
    form.resetField('errorDescription');
    form.clearErrors('areAnyErrorsSelected');
    form.clearErrors('isDurationSelected');
  };

  const handleSubmit = async () => {
    form.setValue('formSubmitErrorText', null);

    if (isSerialNumberEditing && serialNumber) {
      form.setError('serialNumber', {
        message: i18n.t('serial_number_search_error'),
        type: 'manual',
      });
    } else if (deviceTypes.length && !selectedDevice) {
      form.setError('isAnyDeviceSelected', {
        message: i18n.t('required_error'),
      });
    } else if (isArticleEditing && article) {
      form.setError('article', {
        message: i18n.t('article_search_error'),
      });
    } else if (
      deviceType === DeviceType.Special &&
      !isDuringTherapy &&
      !isNotDuringTherapy
    ) {
      form.setError('isDurationSelected', {
        message: i18n.t('required_error'),
      });
    } else if (
      !isSerialNumberEditing &&
      !isArticleEditing &&
      !selectedErrors.length
    ) {
      form.setError('areAnyErrorsSelected', {
        message: i18n.t('required_error'),
      });
    } else {
      const result = await form.trigger();

      if (result) {
        void form.handleSubmitDefault();
      }
    }
  };

  const turnOffSerialNumberEditing = () => {
    setIsSerialNumberEditing(false);
  };

  const turnOffArticleEditing = () => {
    setIsArticleEditing(false);
  };

  const onModalClose = () => {
    if (!!serialNumber) {
      setIsCloseAddingModalShown(true);
    } else {
      handleModalClose();
    }
  };

  const isFirefox = 'InstallTrigger' in window;

  return (
    <Modal
      containerClassName={styles.container}
      contentClassName={clsx(
        styles.overflowYScroll,
        isFirefox && styles.paddingRight24,
      )}
      isOpen={isOpen}
      onClose={onModalClose}
      doNotCloseOnOutsideClick={true}
      title={title}
    >
      <div>
        <div
          className={clsx(
            styles.marginBottom10,
            styles.rowContainer,
            styles.columnGap4,
          )}
        >
          <div className={clsx(styles.subtitle)}>{i18n.t('device')}</div>
          {!isSerialNumberEditing && (
            <div
              className={clsx(
                styles.rowContainer,
                styles.columnGap4,
                styles.resetButton,
              )}
              data-test-id={'reset-device-button'}
              onClick={showClearSerialNumberModal}
            >
              <ResetIcon />
              <div className={clsx(styles.label, styles.fontWeight500)}>
                {i18n.t('reset')}
              </div>
            </div>
          )}
        </div>
        {(device.isLoading && !isDuringTherapy && !isNotDuringTherapy) ||
        isSerialNumberEditing ? (
          <SerialNumberEditing
            form={form}
            device={device}
            turnOffSerialNumberEditing={turnOffSerialNumberEditing}
            serialNumber={serialNumber}
          />
        ) : (
          !sameDevice.isLoading && (
            <div>
              <SerialNumberInfo
                serialNumber={serialNumber}
                form={form}
                device={device.data?.foundDevice}
                dateOfResale={dateOfResale}
                deviceTypes={deviceTypes}
              />
              {!deviceTypes.length && (
                <div
                  className={clsx(
                    styles.marginBottom10,
                    styles.rowContainer,
                    styles.columnGap4,
                  )}
                >
                  <div className={clsx(styles.subtitle)}>
                    {i18n.t('replacement_part')}
                  </div>
                  {!isArticleEditing && (
                    <div
                      className={clsx(
                        styles.rowContainer,
                        styles.columnGap4,
                        styles.resetButton,
                      )}
                      data-test-id={'reset-article-button'}
                      onClick={showClearArticleModal}
                    >
                      <ResetIcon />
                      <div className={clsx(styles.label, styles.fontWeight500)}>
                        {i18n.t('reset')}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!replacementPart.data ||
              replacementPart.isLoading ||
              isArticleEditing ? (
                <ArticleEditing
                  form={form}
                  turnOffArticleEditing={turnOffArticleEditing}
                  replacementPart={replacementPart}
                  isDisabled={!!deviceTypes.length}
                />
              ) : (
                <ArticleInfo
                  deviceType={deviceType}
                  isLoading={replacementPart.isLoading || device.isLoading}
                  article={article}
                  replacementPart={replacementPart.data?.sparePartDescription}
                  form={form}
                  availableErrors={availableErrors}
                  selectedErrors={selectedErrors}
                  removeSelectedError={removeSelectedError}
                  showClearErrorsModal={showClearErrorsModal}
                  errorDescription={errorDescription}
                />
              )}
            </div>
          )
        )}
      </div>
      <div
        className={clsx(
          !!formSubmitErrorText && styles.rowContainer,
          styles.buttonContainer,
          styles.justifyContentFlexEnd,
        )}
      >
        {formSubmitErrorText && (
          <div className={clsx(styles.unexpectedServerError, styles.flex1)}>
            {formSubmitErrorText}
          </div>
        )}
        <div
          className={clsx(
            styles.rowContainer,
            styles.justifyContentFlexEnd,
            styles.columnGap10,
          )}
        >
          <Button
            title={i18n.t('cancel')}
            onClick={onModalClose}
            color={ButtonColor.Secondary}
          />
          <Button
            testId={'add-to-cart-button'}
            title={i18n.t('add_to_cart')}
            loading={isFormLoading}
            disabled={isFormLoading}
            onClick={handleSubmit}
          />
        </div>
      </div>
      <CustomModal
        isOpen={isClearSerialNumberModalShown}
        bodyText={i18n.t('clear_serial_number')}
        onSubmit={clearSerialNumber}
        onCancel={() => setIsClearSerialNumberModalShown(false)}
        cancelButtonText={i18n.t('cancel')}
        submitButtonText={i18n.t('clear')}
        titleText={i18n.t('clear_serial_number_title')}
        containerClassName={styles.modalBodyMinWidth480}
      />
      <CustomModal
        isOpen={isClearArticleModalShown}
        bodyText={i18n.t('clear_article')}
        onSubmit={clearArticle}
        onCancel={() => setIsClearArticleModalShown(false)}
        cancelButtonText={i18n.t('cancel')}
        submitButtonText={i18n.t('clear')}
        titleText={i18n.t('clear_article_title')}
        containerClassName={styles.modalBodyMinWidth480}
      />
      <CustomModal
        isOpen={isClearErrorsModalShown}
        bodyText={i18n.t('clear_error')}
        onSubmit={clearErrors}
        onCancel={() => setIsClearErrorsModalShown(false)}
        cancelButtonText={i18n.t('cancel')}
        submitButtonText={i18n.t('clear')}
        titleText={i18n.t('clear')}
        containerClassName={styles.modalBodyMinWidth480}
      />
      <CustomModal
        isOpen={isCloseAddingModalShown}
        bodyText={i18n.t('leave_add_modal_text')}
        onSubmit={() => {
          setIsCloseAddingModalShown(false);
          void handleSubmit();
        }}
        onClose={() => setIsCloseAddingModalShown(false)}
        onCancel={() => {
          setIsCloseAddingModalShown(false);
          handleModalClose();
        }}
        cancelButtonText={i18n.t('discard_changes')}
        submitButtonText={i18n.t('save_changes')}
        titleText={i18n.t('leave_add_modal_title')}
        containerClassName={styles.modalBodyMinWidth480}
        submitButtonType={ButtonColor.Primary}
        cancelButtonType={ButtonColor.Warning}
        submitButtonTestId={'save-changes-button'}
        cancelButtonTestId={'discard-button'}
      />
    </Modal>
  );
};
